import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Card,
  Form,
  Input,
  Modal,
  Upload,
  Tag,
  App,
  Switch,
  Grid,
  Dropdown,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  SearchOutlined,
  MoreOutlined,
} from '@ant-design/icons'
import { categoriesAPI } from '../services/api'
import { sortByName } from '../utils/searchUtils'

interface Category {
  id: number
  name: string
  description: string
  slug: string
  icon: string
  gamesCount?: number
  isActive: boolean
}

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [form] = Form.useForm()
  const { message, modal } = App.useApp()
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md

  // Load categories from API
  useEffect(() => {
    loadCategories()
  }, [])

  const loadCategories = async () => {
    try {
      setLoading(true)
      const response = await categoriesAPI.getAll(true)
      setCategories(sortByName(response.data || []))
    } catch (error) {
      console.error('Error loading categories:', error)
      message.error('Failed to load categories')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    setEditingCategory(null)
    form.resetFields()
    setIsModalVisible(true)
  }

  const handleEdit = (category: Category) => {
    setEditingCategory(category)
    form.setFieldsValue(category)
    setIsModalVisible(true)
  }

  const handleDelete = async (id: number) => {
    try {
      await categoriesAPI.delete(id.toString())
      message.success('Category deleted successfully')
      loadCategories()
    } catch (error) {
      console.error('Error deleting category:', error)
      message.error('Failed to delete category')
    }
  }

  const handleStatusChange = async (id: number, isActive: boolean) => {
    try {
      await categoriesAPI.update(id.toString(), { isActive })
      message.success('Category status updated successfully')
      loadCategories()
    } catch (error) {
      console.error('Error updating category status:', error)
      message.error('Failed to update category status')
    }
  }

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
  }

  const handleSubmit = async (values: any) => {
    try {
      const slug = generateSlug(values.name)
      const categoryData = {
        ...values,
        slug,
      }
      
      if (editingCategory) {
        // Update existing category
        await categoriesAPI.update(editingCategory.id.toString(), categoryData)
        message.success('Category updated successfully')
      } else {
        // Create new category
        await categoriesAPI.create(categoryData)
        message.success('Category created successfully')
      }
      
      setIsModalVisible(false)
      form.resetFields()
      loadCategories()
    } catch (error) {
      console.error('Error saving category:', error)
      message.error('Failed to save category')
    }
  }

  const filteredCategories = categories.filter(cat => {
    if (statusFilter === 'all') return true
    if (statusFilter === 'active') return cat.isActive
    if (statusFilter === 'inactive') return !cat.isActive
    return true
  })

  const renderActionCell = (_: any, record: Category) => {
    const actionItems = [
      {
        key: 'edit',
        label: 'Edit',
        icon: <EditOutlined />,
        onClick: () => handleEdit(record),
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => {
          modal.confirm({
            title: 'Delete Category',
            content: `Are you sure you want to delete "${record.name}"?`,
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () => handleDelete(record.id),
          })
        },
      },
    ]

    return (
      <Dropdown
        menu={{
          items: actionItems.map((item) => ({
            key: item.key,
            label: item.label,
            icon: item.icon,
            danger: item.danger,
          })),
          onClick: ({ key }) => {
            const item = actionItems.find((i) => i.key === key)
            if (item?.onClick) item.onClick()
          },
        }}
        trigger={['click']}
        placement="bottomRight"
      >
        <Button
          type="text"
          icon={<MoreOutlined style={{ fontSize: 18 }} />}
          onClick={(e) => e.preventDefault()}
          style={{
            width: isMobile ? 32 : 40,
            height: isMobile ? 32 : 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        />
      </Dropdown>
    )
  }

  const fullColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search name"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: any, record: Category) =>
        record.name.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      render: (slug: string) => <Tag color="blue">{slug}</Tag>,
    },
    {
      title: 'Games Count',
      dataIndex: 'gamesCount',
      key: 'gamesCount',
      sorter: (a: Category, b: Category) => (a.gamesCount || 0) - (b.gamesCount || 0),
      render: (count: number) => <Tag color="green">{count || 0}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value: any, record: Category) => record.isActive === value,
      render: (isActive: boolean, record: Category) => (
        <Switch
          checked={isActive}
          onChange={(checked) => handleStatusChange(record.id, checked)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: renderActionCell,
    },
  ]

  const mobileColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <strong>{name}</strong>,
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (_: boolean, record: Category) => (
        <Switch
          checked={record.isActive}
          onChange={(checked) => handleStatusChange(record.id, checked)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: renderActionCell,
    },
  ]

  const columns = isMobile ? mobileColumns : fullColumns

  return (
    <div>
      <Card>
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: isMobile ? 'stretch' : 'center',
          }}
        >
          <h2 style={{ margin: 0 }}>Categories Management</h2>
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Add Category
            </Button>
            <Upload>
              <Button icon={<UploadOutlined />}>Import Categories</Button>
            </Upload>
            <Button
              onClick={() => setStatusFilter('all')}
              type={statusFilter === 'all' ? 'primary' : 'default'}
            >
              All
            </Button>
            <Button
              onClick={() => setStatusFilter('active')}
              type={statusFilter === 'active' ? 'primary' : 'default'}
            >
              Active
            </Button>
            <Button
              onClick={() => setStatusFilter('inactive')}
              type={statusFilter === 'inactive' ? 'primary' : 'default'}
            >
              Inactive
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredCategories}
          rowKey="id"
          loading={loading}
          pagination={{
            total: filteredCategories.length,
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          scroll={isMobile ? undefined : { x: 900 }}
          tableLayout={isMobile ? 'fixed' : 'auto'}
        />
      </Card>

      <Modal
        title={editingCategory ? 'Edit Category' : 'Add Category'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false)
          form.resetFields()
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Category Name"
            rules={[
              { required: true, message: 'Please enter category name' },
              { min: 2, message: 'Name must be at least 2 characters' },
            ]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { required: true, message: 'Please enter description' },
              { min: 10, message: 'Description must be at least 10 characters' },
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter category description"
            />
          </Form.Item>

          <Form.Item
            name="icon"
            label="Icon URL"
            rules={[
              { required: true, message: 'Please enter icon URL' },
              { type: 'url', message: 'Please enter a valid URL' },
            ]}
          >
            <Input placeholder="Enter icon URL" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setIsModalVisible(false)
                form.resetFields()
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingCategory ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Categories
